import {
  Button,
  ChevronIcon,
  ChevronUpIcon,
  MessageBox,
  ModalDialog,
  Table,
  TableCell,
  TableHeadCell,
  TableRow,
  Tooltip,
} from "@rothoblaas/design-system";
import { useMemo, type FC } from "react";
import { useFormContext } from "react-hook-form";
import ExpansionPanel from "../../../components/ui/ExpansionPanel";
import Icon from "../../../components/ui/Icon";
import useI18n from "../../../hooks/useTranslations";
import type { ConnectorFamilyForSettings } from "../../../http/deckDesigner/types/connectorFamilies";
import type { SecondaryItem } from "../../../http/deckDesigner/types/secondaryItems";
import type { Settings } from "../../../http/deckDesigner/types/settings";
import type { SubstructureMaterialForSettings } from "../../../http/deckDesigner/types/substructureMaterials";
import type { SubstructureForSettings } from "../../../http/deckDesigner/types/substructures";
import type { SupportFamilyForSettings } from "../../../http/deckDesigner/types/supportFamilies";
import { getSecondMomentOfArea } from "../../../http/deckDesigner/formulas";
import type { DeckDesignerConfigurationFormValues } from "../../../http/deckDesigner/useAutoSaveDeckDesignerConfiguration";
import styles from "./ConnectorsCard.module.css";
import { formatSubstructureOptionLabel, useMeasurementFormat } from "./measurementFormat";
import { getNumberFormat } from "../../../utils/numberFormat";
import type { SupportCompatibilityRow, SupportSpacingRow } from "./useSupportOptions";

export type CompatibilityModalType = "connector" | "support" | "spacing";

// Reference table behind the "why is this empty" banners - grouped by family, ignoring the values the
// user hasn't set yet (substructure material, gap, board thickness), scoped only to the
// fastening/grooving type that's already fixed at this point in the flow.
type ConnectorCompatibilityRow = {
  family: ConnectorFamilyForSettings;
  substructureMaterials: SubstructureMaterialForSettings[];
  minimumGap: number;
  maximumGap: number;
  minConnectorThickness: number | null;
  maxConnectorThickness: number | null;
};

// Grouped by family for display - a flat list of every item across every family reads as noise, so
// the modal collapses each family behind its own expansion panel instead.
type SupportCompatibilityGroup<Row> = {
  supportFamily: SupportFamilyForSettings;
  rows: Row[];
};

// Families in the order their first row comes, so the closest suggestions' families lead.
const groupBySupportFamily = <Row extends { supportFamily: SupportFamilyForSettings }>(
  rows: Row[],
): SupportCompatibilityGroup<Row>[] => {
  const groupsByFamilyCode = new Map<string, SupportCompatibilityGroup<Row>>();

  rows.forEach((row) => {
    const existing = groupsByFamilyCode.get(row.supportFamily.code);

    if (existing) {
      existing.rows.push(row);
    } else {
      groupsByFamilyCode.set(row.supportFamily.code, { supportFamily: row.supportFamily, rows: [row] });
    }
  });

  return Array.from(groupsByFamilyCode.values());
};

type CompatibilityModalProps = {
  type: CompatibilityModalType;
  settings: Settings | undefined;
  fasteningTypeCode: string | undefined;
  groovingTypeCode: string | undefined;
  isSymmetricalGrooving: boolean;
  isAsymmetricalGrooving: boolean;
  substructure: SubstructureForSettings | null;
  secondaryItem: SecondaryItem | null;
  boardThickness: number | null;
  totalHeight: number | null;
  slopeDegrees: number | null;
  joistsSpacing: number | null;
  deflection: number | null;
  liveLoad: number | null;
  supportCompatibilityRows: SupportCompatibilityRow[];
  supportSpacingRows: SupportSpacingRow[];
  onClose: () => void;
};

// The compatible values behind ConnectorsCard's empty-list banners: per connector family, or per
// support with the slope/H suggestion "Apply" writes into the form, or per support with the section and
// joist spacing (deflection and live load last) giving it room along the joists.
const CompatibilityModal: FC<CompatibilityModalProps> = ({
  type,
  settings,
  fasteningTypeCode,
  groovingTypeCode,
  isSymmetricalGrooving,
  isAsymmetricalGrooving,
  substructure,
  secondaryItem,
  boardThickness,
  totalHeight,
  slopeDegrees,
  joistsSpacing,
  deflection,
  liveLoad,
  supportCompatibilityRows,
  supportSpacingRows,
  onClose,
}) => {
  const i18n = useI18n();
  const measurementFormat = useMeasurementFormat();
  const { setValue } = useFormContext<DeckDesignerConfigurationFormValues>();
  const calculationTranslation = i18n.translation.deckDesignerModule.configurations.calculation;
  const connectorMappings = settings?.connectorMappings;
  const connectors = settings?.connectors;

  const formatMm = (value: number) =>
    measurementFormat.length(value, "mm", `${value} ${i18n.translation.common.unitMm}`);

  const formatRange = (min: number | null, max: number | null, unit: "mm" | "cm"): string => {
    const unitText = ` ${unit === "cm" ? i18n.translation.common.unitCm : i18n.translation.common.unitMm}`;
    // In metric the unit is only written after the range's last value, in imperial after each one.
    const format = (value: number, withUnit = true) =>
      measurementFormat.length(value, unit, withUnit ? `${value}${unitText}` : `${value}`);

    if (min == null && max == null) {
      return "–";
    }

    if (min == null) {
      return `≤ ${format(max!)}`;
    }

    if (max == null) {
      return `≥ ${format(min)}`;
    }

    return min === max ? format(min) : `${format(min, false)}–${format(max)}`;
  };

  // Board thickness (symmetrical/without grooving) or grooving height (asymmetrical) range that
  // makes this family selectable - mirrors the same formulas used in useConnectorOptions' filters and
  // in BoardsCard's own validateBoardThickness, just solved for display instead of for a boolean.
  const getConnectorFamilyRangeLabel = (family: ConnectorFamilyForSettings): string => {
    if (isSymmetricalGrooving) {
      if (family.groovingThickness == null || family.groovingMinHeight == null || family.groovingMaxHeight == null) {
        return "–";
      }

      const minBoardThickness = family.groovingMinHeight * 2 + family.groovingThickness;
      const maxBoardThickness = family.groovingMaxHeight * 2 + family.groovingThickness;
      const cappedMaxBoardThickness =
        family.maxBoardThickness != null ? Math.min(maxBoardThickness, family.maxBoardThickness) : maxBoardThickness;

      return formatRange(minBoardThickness, cappedMaxBoardThickness, "mm");
    }

    if (isAsymmetricalGrooving) {
      return formatRange(family.groovingMinHeight, family.groovingMaxHeight, "mm");
    }

    return formatRange(null, family.maxBoardThickness, "mm");
  };

  const connectorFamilyRangeHeader = isAsymmetricalGrooving
    ? calculationTranslation.groovingHeight
    : calculationTranslation.boardThickness;

  const connectorCompatibilityRows = useMemo((): ConnectorCompatibilityRow[] => {
    if (type !== "connector" || !connectorMappings || !fasteningTypeCode || !groovingTypeCode) {
      return [];
    }

    const rowsByFamilyCode = new Map<string, ConnectorCompatibilityRow>();

    connectorMappings
      .filter(
        (mapping) => mapping.fasteningType.code === fasteningTypeCode && mapping.groovingType.code === groovingTypeCode,
      )
      .forEach((mapping) => {
        const existing = rowsByFamilyCode.get(mapping.connectorFamily.code);

        if (!existing) {
          rowsByFamilyCode.set(mapping.connectorFamily.code, {
            family: mapping.connectorFamily,
            substructureMaterials: [...mapping.substructureMaterials],
            minimumGap: mapping.minimumGap,
            maximumGap: mapping.maximumGap,
            minConnectorThickness: null,
            maxConnectorThickness: null,
          });
          return;
        }

        existing.minimumGap = Math.min(existing.minimumGap, mapping.minimumGap);
        existing.maximumGap = Math.max(existing.maximumGap, mapping.maximumGap);
        mapping.substructureMaterials.forEach((material) => {
          if (!existing.substructureMaterials.some((option) => option.code === material.code)) {
            existing.substructureMaterials.push(material);
          }
        });
      });

    // Connector thickness is a per-item property (it can vary within a family), not a per-mapping
    // one - folded in as a min-max range per family so it can be compared against the height
    // budget in the stratigraphy summary above the table.
    connectors?.forEach((option) => {
      const row = rowsByFamilyCode.get(option.connectorFamily.code);

      if (!row) {
        return;
      }

      row.minConnectorThickness =
        row.minConnectorThickness == null ? option.thickness : Math.min(row.minConnectorThickness, option.thickness);
      row.maxConnectorThickness =
        row.maxConnectorThickness == null ? option.thickness : Math.max(row.maxConnectorThickness, option.thickness);
    });

    return Array.from(rowsByFamilyCode.values());
  }, [connectorMappings, connectors, fasteningTypeCode, groovingTypeCode, type]);

  // Only rows with a configuration at or below the current H; locked rows stay so their family
  // still shows why it's unavailable.
  const supportCompatibilityGroups = useMemo(
    () =>
      groupBySupportFamily(
        supportCompatibilityRows.filter((row) => row.isSlopeLocked || row.suggestedTotalHeightCm != null),
      ),
    [supportCompatibilityRows],
  );

  const supportSpacingGroups = useMemo(() => groupBySupportFamily(supportSpacingRows), [supportSpacingRows]);
  const decimalFormat = getNumberFormat(i18n.translation.languageCode, { maximumFractionDigits: 3 });
  const formatMeters = (value: number) =>
    measurementFormat.length(value, "m", `${decimalFormat.format(value)} ${i18n.translation.common.unitM}`);
  const comparedValueTooltips = {
    unchanged: calculationTranslation.suggestedValueUnchanged,
    reduced: calculationTranslation.suggestedValueReduced,
    increased: calculationTranslation.suggestedValueIncreased,
  };

  const hasAnyCompatibleSupport = supportCompatibilityRows.some((row) => row.isCompatible);

  // Only blame the slope in the empty-state message when it's actually the reason nothing fits -
  // i.e. at least one item would work if the slope were flat. Otherwise the real cause is elsewhere
  // (H out of range for every product, or a NAG/GRANULO divisibility mismatch that fails even flat).
  const isSlopeTheCauseOfNoCompatibleSupport =
    !hasAnyCompatibleSupport && supportCompatibilityRows.some((row) => row.isCompatibleIgnoringSlope);

  // The stack that's already fixed regardless of which connector/support gets picked - what's left
  // of the target total height (H) after this is the room the connector and support have to share.
  // Mirrors calculateValidSupportRange's own stack, minus the connector/support terms it solves for.
  const totalHeightMm = totalHeight == null ? null : totalHeight * 10;
  const stratigraphyBaseHeightMm =
    (substructure?.height ?? 0) + (secondaryItem?.thickness ?? 0) + (boardThickness ?? 0);
  const availableHeightForConnectorAndSupportMm =
    totalHeightMm == null ? null : totalHeightMm - stratigraphyBaseHeightMm;

  // A suggested value next to the one the user entered: green when it matches, red with an arrow when
  // "Apply" would raise or lower it, and a tooltip saying which.
  const renderComparedValue = (
    text: string,
    value: number | null,
    enteredValue: number | null,
    tooltips: { unchanged: string; reduced: string; increased: string },
  ) => {
    if (value == null || enteredValue == null) {
      return text;
    }

    const difference = value === enteredValue ? 0 : value - enteredValue;

    return (
      <Tooltip
        className={styles.comparedValueTooltip}
        content={difference === 0 ? tooltips.unchanged : difference < 0 ? tooltips.reduced : tooltips.increased}
      >
        <span className={difference === 0 ? styles.valueUnchanged : styles.valueChanged}>
          {difference !== 0 && (
            <Icon glyph={difference < 0 ? ChevronIcon : ChevronUpIcon} className={styles.comparedValueArrow} />
          )}
          {text}
        </span>
      </Tooltip>
    );
  };

  const applySupportSuggestion = (row: SupportCompatibilityRow) => {
    if (row.suggestedTotalHeightCm == null) {
      return;
    }

    const options = { shouldDirty: true, shouldValidate: true };
    setValue("slopeDegrees", row.suggestedSlope, options);
    // Flat terrain has no arrow (no slope is saved).
    if (row.suggestedSlope <= 0) {
      setValue("slopeArrow", null, { shouldDirty: true });
    }
    setValue("totalHeight", row.suggestedTotalHeightCm, options);
    onClose();
  };

  const applySupportSpacingSuggestion = (row: SupportSpacingRow) => {
    const options = { shouldDirty: true, shouldValidate: true };

    if (row.substructure.id !== substructure?.id) {
      // As the section field itself does: the support is picked again for the new section.
      setValue("support", null, { shouldDirty: true });
      setValue("substructure", row.substructure, options);
    }
    setValue("joistsSpacing", row.joistsSpacing, options);
    setValue("deflection", row.deflection, options);
    setValue("liveLoad", row.liveLoad, options);
    onClose();
  };

  // Stiffness tells a bigger section from a smaller one.
  const currentSecondMomentOfArea = substructure ? (getSecondMomentOfArea(substructure) ?? 0) : 0;

  return (
    <ModalDialog
      fitContent
      onClose={onClose}
      title={`${calculationTranslation.showCompatibleValues} – ${
        type === "connector"
          ? i18n.translation.deckDesignerModule.connectorFamilies.plural
          : i18n.translation.deckDesignerModule.supportFamilies.plural
      }`}
    >
      {type === "spacing" ? (
        <>
          <MessageBox
            type="informative"
            className={styles.noCompatibleSupportWarning}
            content={
              supportSpacingRows.length
                ? calculationTranslation.supportSpacingSuggestionsInfo
                : calculationTranslation.noSupportSpacingSuggestion
            }
          />
          <div className={styles.supportCompatibilityGroups}>
            {supportSpacingGroups.map((group) => (
              <ExpansionPanel key={group.supportFamily.code} title={group.supportFamily.name}>
                <Table>
                  <thead>
                    <TableRow>
                      <TableHeadCell align="left">{i18n.translation.common.code}</TableHeadCell>
                      <TableHeadCell align="left">{calculationTranslation.supportWidth}</TableHeadCell>
                      <TableHeadCell align="left">
                        {calculationTranslation.substructureSection(i18n.translation.common.unitMm)}
                      </TableHeadCell>
                      <TableHeadCell align="left">{calculationTranslation.joistsSpacing}</TableHeadCell>
                      <TableHeadCell align="left">{calculationTranslation.deflection}</TableHeadCell>
                      <TableHeadCell align="left">{calculationTranslation.liveLoad}</TableHeadCell>
                      <TableHeadCell align="left">{calculationTranslation.supportSpacing}</TableHeadCell>
                      <TableHeadCell align="left" />
                    </TableRow>
                  </thead>
                  <tbody>
                    {group.rows.map((row) => {
                      const isSameSection = row.substructure.id === substructure?.id;
                      const isUnchanged =
                        isSameSection &&
                        row.joistsSpacing === joistsSpacing &&
                        row.deflection === deflection &&
                        row.liveLoad === liveLoad;

                      return (
                        <TableRow key={row.itemCode}>
                          <TableCell>{row.itemCode}</TableCell>
                          <TableCell>{formatMm(row.width)}</TableCell>
                          <TableCell>
                            {renderComparedValue(
                              formatSubstructureOptionLabel(
                                row.substructure,
                                measurementFormat,
                                i18n.translation.common,
                              ),
                              isSameSection
                                ? currentSecondMomentOfArea
                                : (getSecondMomentOfArea(row.substructure) ?? 0),
                              currentSecondMomentOfArea,
                              comparedValueTooltips,
                            )}
                          </TableCell>
                          <TableCell>
                            {renderComparedValue(
                              formatMeters(row.joistsSpacing),
                              row.joistsSpacing,
                              joistsSpacing,
                              comparedValueTooltips,
                            )}
                          </TableCell>
                          <TableCell>
                            {renderComparedValue(
                              `L/${row.deflection}`,
                              row.deflection,
                              deflection,
                              comparedValueTooltips,
                            )}
                          </TableCell>
                          <TableCell>
                            {renderComparedValue(
                              measurementFormat.liveLoad(
                                row.liveLoad,
                                `${row.liveLoad} ${i18n.translation.common.unitKnM2}`,
                              ),
                              row.liveLoad,
                              liveLoad,
                              comparedValueTooltips,
                            )}
                          </TableCell>
                          <TableCell>{formatMeters(row.supportSpacing)}</TableCell>
                          <TableCell>
                            {!isUnchanged && (
                              <Button variant="primary" onClick={() => applySupportSpacingSuggestion(row)}>
                                {calculationTranslation.supportApplySuggestion}
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </tbody>
                </Table>
              </ExpansionPanel>
            ))}
          </div>
        </>
      ) : (
        <>
          <div className={styles.stratigraphySummary}>
            <p className={styles.stratigraphySummaryTitle}>{calculationTranslation.stratigraphySummaryTitle}</p>
            <p>
              {i18n.translation.common.substructureHeight}:{" "}
              {substructure?.height != null ? formatMm(substructure.height) : "–"}
            </p>
            <p>
              {i18n.translation.deckDesignerModule.secondaryItems.singular} ({i18n.translation.common.thickness}):{" "}
              {secondaryItem?.thickness != null ? formatMm(secondaryItem.thickness) : "–"}
            </p>
            <p>
              {calculationTranslation.boardThickness}: {boardThickness != null ? formatMm(boardThickness) : "–"}
            </p>
            <p>
              {calculationTranslation.totalHeight}:{" "}
              {totalHeight != null
                ? measurementFormat.length(totalHeight, "cm", `${totalHeight} ${i18n.translation.common.unitCm}`)
                : "–"}
            </p>
            <p>
              <strong>{calculationTranslation.availableHeightForConnectorAndSupport}:</strong>{" "}
              {availableHeightForConnectorAndSupportMm != null
                ? formatMm(availableHeightForConnectorAndSupportMm)
                : "–"}
            </p>
          </div>
          {type === "connector" ? (
            <Table>
              <thead>
                <TableRow>
                  <TableHeadCell align="left">{i18n.translation.common.name}</TableHeadCell>
                  <TableHeadCell align="left">
                    {i18n.translation.deckDesignerModule.substructureMaterials.plural}
                  </TableHeadCell>
                  <TableHeadCell align="left">{calculationTranslation.gap}</TableHeadCell>
                  <TableHeadCell align="left">{connectorFamilyRangeHeader}</TableHeadCell>
                  <TableHeadCell align="left">{calculationTranslation.connectorThickness}</TableHeadCell>
                  <TableHeadCell align="left">{calculationTranslation.remainingHeightForSupport}</TableHeadCell>
                </TableRow>
              </thead>
              <tbody>
                {connectorCompatibilityRows.map((row) => (
                  <TableRow key={row.family.code}>
                    <TableCell>{row.family.name}</TableCell>
                    <TableCell>{row.substructureMaterials.map((material) => material.name).join(", ")}</TableCell>
                    <TableCell>{formatRange(row.minimumGap, row.maximumGap, "mm")}</TableCell>
                    <TableCell>{getConnectorFamilyRangeLabel(row.family)}</TableCell>
                    <TableCell>{formatRange(row.minConnectorThickness, row.maxConnectorThickness, "mm")}</TableCell>
                    <TableCell>
                      {availableHeightForConnectorAndSupportMm == null
                        ? "–"
                        : formatRange(
                            row.maxConnectorThickness == null
                              ? null
                              : availableHeightForConnectorAndSupportMm - row.maxConnectorThickness,
                            row.minConnectorThickness == null
                              ? null
                              : availableHeightForConnectorAndSupportMm - row.minConnectorThickness,
                            "mm",
                          )}
                    </TableCell>
                  </TableRow>
                ))}
              </tbody>
            </Table>
          ) : (
            <>
              {supportCompatibilityRows.length > 0 && !hasAnyCompatibleSupport && (
                <MessageBox
                  type="warning"
                  className={styles.noCompatibleSupportWarning}
                  content={
                    isSlopeTheCauseOfNoCompatibleSupport
                      ? calculationTranslation.noSupportCompatibleWithSlope
                      : calculationTranslation.noSupportCompatibleGeneric
                  }
                />
              )}
              <div className={styles.supportCompatibilityGroups}>
                {supportCompatibilityGroups.map((group) => {
                  // Locked families (NAG/GRANULO on a slope) stay collapsed and disabled, with the reason.
                  const isLocked = group.rows.every((row) => row.isSlopeLocked);

                  return (
                    <ExpansionPanel
                      key={group.supportFamily.code}
                      title={group.supportFamily.name}
                      disabled={isLocked}
                      meta={isLocked ? calculationTranslation.supportFlatTerrainOnly : undefined}
                    >
                      <Table>
                        <thead>
                          <TableRow>
                            <TableHeadCell align="left">{i18n.translation.common.code}</TableHeadCell>
                            <TableHeadCell align="left">{calculationTranslation.supportMinHeight}</TableHeadCell>
                            <TableHeadCell align="left">{calculationTranslation.supportMaxHeight}</TableHeadCell>
                            <TableHeadCell align="left">{calculationTranslation.supportSuggestedSlope}</TableHeadCell>
                            <TableHeadCell align="left">{calculationTranslation.supportCompatibleHRange}</TableHeadCell>
                            <TableHeadCell align="left" />
                          </TableRow>
                        </thead>
                        <tbody>
                          {group.rows.map((row) => (
                            <TableRow key={row.itemCode}>
                              <TableCell>{row.itemCode}</TableCell>
                              <TableCell>{formatMm(row.minHeight)}</TableCell>
                              <TableCell>{formatMm(row.maxHeight)}</TableCell>
                              <TableCell>
                                {renderComparedValue(
                                  `${row.suggestedSlope} ${i18n.translation.common.unitDeg}`,
                                  row.suggestedSlope,
                                  slopeDegrees ?? 0,
                                  {
                                    unchanged: calculationTranslation.supportSlopeUnchanged,
                                    reduced: calculationTranslation.supportSlopeReduced,
                                    increased: calculationTranslation.supportSlopeIncreased,
                                  },
                                )}
                              </TableCell>
                              <TableCell>
                                {renderComparedValue(
                                  formatRange(row.suggestedTotalHeightCm, row.suggestedTotalHeightCm, "cm"),
                                  row.suggestedTotalHeightCm,
                                  totalHeight,
                                  {
                                    unchanged: calculationTranslation.supportTotalHeightUnchanged,
                                    reduced: calculationTranslation.supportTotalHeightReduced,
                                    increased: calculationTranslation.supportTotalHeightIncreased,
                                  },
                                )}
                              </TableCell>
                              <TableCell>
                                {row.suggestedTotalHeightCm != null && (
                                  <Button variant="primary" onClick={() => applySupportSuggestion(row)}>
                                    {calculationTranslation.supportApplySuggestion}
                                  </Button>
                                )}
                              </TableCell>
                            </TableRow>
                          ))}
                        </tbody>
                      </Table>
                    </ExpansionPanel>
                  );
                })}
              </div>
            </>
          )}
        </>
      )}
    </ModalDialog>
  );
};

export default CompatibilityModal;
